import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { Layout } from '../components/Layout';
import { organizationService, userService, superAdminService } from '../api';
import KnowledgeBaseTab from './organization/KnowledgeBaseTab';
import { Button, Input, ThemedText, Pagination, ActionMenu, ConfirmDialog, Avatar, SkeletonTable } from '../components';
import { EmptyState as SharedEmptyState } from '../components';
import { UserRole, getAvailableRoles, getRoleName } from '../constants/roles';
import { usePagination } from '../hooks/usePagination';
import { ArrowLeft, Pencil, X, Search, Users } from 'lucide-react';
import { TableContainer as SharedTableContainer, Table as SharedTable, Thead as SharedThead, Th as SharedTh, Tbody as SharedTbody, Tr as SharedTr, Td as SharedTd } from '../components/TableStyles';
import { ModalOverlay as Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../components/ModalStyles';
import { StatusBadge as SharedStatusBadge } from '../components/Badge';
import { SearchContainer, StyledSearchContainer, SearchIcon, TransparentInput } from '../components/SearchStyles';
import { FormGroup, FormLabel as Label, StyledSelect as Select } from '../components/FormStyles';
import { ToggleSwitch, ToggleInput, ToggleSlider } from '../components/ToggleStyles';

// --- Styled Components ---

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: ${props => props.theme.colors.tint};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-bottom: ${props => props.theme.spacing.md};

  &:hover {
    text-decoration: underline;
  }
`;

const OrgHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};
`;

const OrgTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const LocalStatusBadge = styled.span`
  display: inline-block;
  padding: 3px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => props.$active ? '#22c55e18' : '#ef444418'};
  color: ${props => props.$active ? '#22c55e' : '#ef4444'};
`;

const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid ${props => props.theme.colors.border};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Tab = styled.button`
  padding: 10px 20px;
  background: none;
  border: none;
  border-bottom: 2px solid ${props => props.$active ? props.theme.colors.tint : 'transparent'};
  margin-bottom: -2px;
  color: ${props => props.$active ? props.theme.colors.tint : props.theme.colors.muted};
  font-size: 14px;
  font-weight: ${props => props.$active ? 600 : 400};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: ${props => props.theme.colors.tint};
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: 16px 20px;
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 13px;
`;

const InfoLabel = styled.span`
  color: ${props => props.theme.colors.muted};
`;

const InfoValue = styled.span`
  color: ${props => props.theme.colors.text};
  font-weight: 500;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 14px 16px;
  border: 1px solid ${props => props.theme.colors.border};
  text-align: center;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.15s ease;

  ${props => props.$clickable && `
    &:hover {
      border-color: ${props.theme.colors.tint};
      transform: translateY(-1px);
    }
  `}
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.muted};
  margin-top: 4px;
`;

const Panel = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  overflow: hidden;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const PanelTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const LocalEmptyState = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-size: 13px;
  color: ${props => props.theme.colors.muted};
`;

const LoadingState = styled.div`
  padding: 48px 16px;
  text-align: center;
  font-size: 14px;
  color: ${props => props.theme.colors.muted};
`;

const SubmissionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const LicenseBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${props => {
    switch (props.$status) {
      case 'Active': return '#22c55e18';
      case 'Trial': return '#3b82f618';
      case 'Expired': return '#ef444418';
      case 'Suspended': return '#f59e0b18';
      default: return props.theme.colors.border;
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'Active': return '#22c55e';
      case 'Trial': return '#3b82f6';
      case 'Expired': return '#ef4444';
      case 'Suspended': return '#f59e0b';
      default: return props.theme.colors.muted;
    }
  }};
`;

// --- Customer table styles ---

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: ${props => props.theme.colors.background};
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.theme.colors.muted};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  white-space: nowrap;
`;

const Tr = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 10px 16px;
  font-size: 13px;
  color: ${props => props.theme.colors.text};
  vertical-align: middle;
`;

const SearchInput = styled.input`
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 6px 12px;
  font-size: 13px;
  outline: none;
  min-width: 200px;

  &:focus {
    border-color: ${props => props.theme.colors.tint};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.tint}20;
  }

  &::placeholder {
    color: ${props => props.theme.colors.muted};
  }
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid ${props => props.theme.colors.border};
  font-size: 13px;
  color: ${props => props.theme.colors.muted};
`;

const PaginationButton = styled.button`
  background: none;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.disabled ? props.theme.colors.muted : props.theme.colors.tint};
  padding: 4px 12px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 13px;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.tint}08;
  }
`;

const AccessDenied = styled.div`
  text-align: center;
  padding: 64px 24px;
  color: ${props => props.theme.colors.muted};
  font-size: 16px;
`;

const RoleBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  background-color: ${props => {
    if (props.role === 0) return '#ff6b6b20';
    if (props.role === 1) return '#4dabf720';
    return '#51cf6620';
  }};
  color: ${props => {
    if (props.role === 0) return '#ff6b6b';
    if (props.role === 1) return '#4dabf7';
    return '#51cf66';
  }};
`;

// --- Helpers ---

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

// --- Component ---

export const OrganizationDetailPage = () => {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const { isSuperAdmin, userRole } = useRole();

  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Edit org modal state
  const [isEditOrgModalOpen, setIsEditOrgModalOpen] = useState(false);
  const [editOrgFormData, setEditOrgFormData] = useState({
    name: '', description: '', contactEmail: '', contactPhone: '', address: '', isInternalTaskOrganization: false,
  });

  // Customer tab state
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerPage, setCustomerPage] = useState(1);
  const [customerTotal, setCustomerTotal] = useState(0);
  const [customerTotalPages, setCustomerTotalPages] = useState(0);
  const customerPageSize = 20;

  // Users tab state
  const {
    currentPage: userCurrentPage,
    totalCount: userTotalCount,
    totalPages: userTotalPages,
    startIndex: userStartIndex,
    endIndex: userEndIndex,
    goToPage: userGoToPage,
    resetPage: userResetPage,
    setTotalCount: setUserTotalCount,
    paginationParams: userPaginationParams,
    extractPagedData: extractUserPagedData,
  } = usePagination(20);

  const [orgUsers, setOrgUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userIncludeInactive, setUserIncludeInactive] = useState(false);

  // User CRUD modal state
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteUserConfirmId, setDeleteUserConfirmId] = useState(null);
  const [userFormData, setUserFormData] = useState({
    email: '', firstName: '', lastName: '', password: '',
    organizationId: parseInt(id), userRole: UserRole.USER, phone: '',
  });

  // Fetch summary
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const data = await superAdminService.getOrganizationSummary(id);
        setSummary(data);
      } catch (err) {
        console.error('Failed to fetch organization summary:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [id]);

  // Fetch customers when tab is active
  useEffect(() => {
    if (activeTab !== 'customers') return;
    const fetchCustomers = async () => {
      setCustomersLoading(true);
      try {
        const data = await superAdminService.getOrganizationCustomers(id, {
          search: customerSearch,
          page: customerPage,
          pageSize: customerPageSize,
        });
        setCustomers(data.items?.$values || data.items || []);
        setCustomerTotal(data.totalCount || 0);
        setCustomerTotalPages(data.totalPages || 0);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
      } finally {
        setCustomersLoading(false);
      }
    };
    fetchCustomers();
  }, [id, activeTab, customerSearch, customerPage]);

  // Fetch users when tab is active
  useEffect(() => {
    if (activeTab !== 'users') return;
    fetchOrgUsers();
  }, [id, activeTab, userIncludeInactive, userCurrentPage]);

  // Reset user page on filter change
  useEffect(() => {
    userResetPage();
  }, [userIncludeInactive, userSearchTerm]);

  const fetchOrgUsers = async () => {
    setUsersLoading(true);
    try {
      const params = {
        organizationId: parseInt(id),
        includeInactive: userIncludeInactive,
        ...userPaginationParams,
      };
      const response = await userService.getUsers(params);
      const { items, totalCount } = extractUserPagedData(response);
      setOrgUsers(items);
      setUserTotalCount(totalCount);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const refreshSummary = async () => {
    try {
      const data = await superAdminService.getOrganizationSummary(id);
      setSummary(data);
    } catch (err) {
      console.error('Failed to refresh summary:', err);
    }
  };

  // --- Edit Org ---
  const openEditOrgModal = () => {
    if (!summary) return;
    setEditOrgFormData({
      name: summary.name || '',
      description: summary.description || '',
      contactEmail: summary.contactEmail || '',
      contactPhone: summary.contactPhone || '',
      address: summary.address || '',
      isInternalTaskOrganization: summary.isInternalTaskOrganization || false,
    });
    setIsEditOrgModalOpen(true);
  };

  const handleUpdateOrganization = async (e) => {
    e.preventDefault();
    const validation = organizationService.validateUpdateData(editOrgFormData);
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }
    try {
      await organizationService.updateOrganization(parseInt(id), editOrgFormData);
      toast.success('Organization updated successfully');
      setIsEditOrgModalOpen(false);
      refreshSummary();
    } catch (error) {
      console.error('Failed to update organization:', error);
      toast.error(error.response?.data?.message || 'Failed to update organization');
    }
  };

  // --- User CRUD ---
  const handleCreateUser = async (e) => {
    e.preventDefault();
    const userData = { ...userFormData, organizationId: parseInt(id) };
    const validation = userService.validateCreateUserData(userData);
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }
    try {
      await userService.createUser(userData);
      toast.success('User created successfully');
      setIsCreateUserModalOpen(false);
      resetUserForm();
      fetchOrgUsers();
      refreshSummary();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    const updateData = {
      firstName: userFormData.firstName,
      lastName: userFormData.lastName,
      phone: userFormData.phone,
    };
    const validation = userService.validateProfileData(updateData);
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }
    try {
      await userService.updateUser(selectedUser.id, updateData);
      toast.success('User updated successfully');
      setIsEditUserModalOpen(false);
      resetUserForm();
      fetchOrgUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleUpdateRole = async (e) => {
    e.preventDefault();
    try {
      await userService.updateUserRole(selectedUser.id, { userRole: parseInt(userFormData.userRole) });
      toast.success('User role updated successfully');
      setIsRoleModalOpen(false);
      resetUserForm();
      fetchOrgUsers();
    } catch (error) {
      console.error('Failed to update user role:', error);
      toast.error(error.response?.data?.message || 'Failed to update user role');
    }
  };

  const handleToggleUserActive = async (user) => {
    try {
      if (user.isActive) {
        await userService.deactivateUser(user.id);
        toast.success('User deactivated');
      } else {
        await userService.activateUser(user.id);
        toast.success('User activated');
      }
      fetchOrgUsers();
      refreshSummary();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await userService.deleteUser(userId);
      toast.success('User deleted successfully');
      setDeleteUserConfirmId(null);
      fetchOrgUsers();
      refreshSummary();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const openEditUserModal = (user) => {
    setSelectedUser(user);
    setUserFormData(prev => ({
      ...prev,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: user.phone || '',
    }));
    setIsEditUserModalOpen(true);
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setUserFormData(prev => ({
      ...prev,
      userRole: user.userRole !== undefined ? user.userRole : user.role,
    }));
    setIsRoleModalOpen(true);
  };

  const resetUserForm = () => {
    setUserFormData({
      email: '', firstName: '', lastName: '', password: '',
      organizationId: parseInt(id), userRole: UserRole.USER, phone: '',
    });
    setSelectedUser(null);
  };

  const filteredOrgUsers = orgUsers.filter(user =>
    user.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.firstName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  // --- Access check ---
  if (!isSuperAdmin()) {
    return (
      <Layout currentPath={`/organizations/${id}`}>
        <AccessDenied theme={theme}>Access Denied</AccessDenied>
      </Layout>
    );
  }

  // --- Render tabs ---
  const renderOverview = () => {
    if (!summary) return null;

    return (
      <>
        <StatsGrid theme={theme}>
          <StatCard
            theme={theme}
            $clickable
            onClick={() => setActiveTab('users')}
            title="View users"
          >
            <StatValue theme={theme}>{summary.totalUsers}</StatValue>
            <StatLabel theme={theme}>Total Users</StatLabel>
          </StatCard>
          <StatCard theme={theme}>
            <StatValue theme={theme}>{summary.totalForms}</StatValue>
            <StatLabel theme={theme}>Active Forms</StatLabel>
          </StatCard>
          <StatCard theme={theme}>
            <StatValue theme={theme}>{summary.totalSubmissions}</StatValue>
            <StatLabel theme={theme}>Total Submissions</StatLabel>
          </StatCard>
          <StatCard theme={theme}>
            <StatValue theme={theme}>{formatBytes(summary.storageUsedBytes)}</StatValue>
            <StatLabel theme={theme}>Storage</StatLabel>
          </StatCard>
        </StatsGrid>

        <CardGrid theme={theme}>
          <Card theme={theme}>
            <CardTitle theme={theme}>Organization Info</CardTitle>
            <InfoRow><InfoLabel theme={theme}>Name</InfoLabel><InfoValue theme={theme}>{summary.name}</InfoValue></InfoRow>
            {summary.description && (
              <InfoRow><InfoLabel theme={theme}>Description</InfoLabel><InfoValue theme={theme}>{summary.description}</InfoValue></InfoRow>
            )}
            {summary.contactEmail && (
              <InfoRow><InfoLabel theme={theme}>Email</InfoLabel><InfoValue theme={theme}>{summary.contactEmail}</InfoValue></InfoRow>
            )}
            {summary.contactPhone && (
              <InfoRow><InfoLabel theme={theme}>Phone</InfoLabel><InfoValue theme={theme}>{summary.contactPhone}</InfoValue></InfoRow>
            )}
            {summary.address && (
              <InfoRow><InfoLabel theme={theme}>Address</InfoLabel><InfoValue theme={theme}>{summary.address}</InfoValue></InfoRow>
            )}
            <InfoRow><InfoLabel theme={theme}>Created</InfoLabel><InfoValue theme={theme}>{formatDate(summary.createdAt)}</InfoValue></InfoRow>
          </Card>

          <Card theme={theme}>
            <CardTitle theme={theme}>License</CardTitle>
            {summary.licensePlanName ? (
              <>
                <InfoRow>
                  <InfoLabel theme={theme}>Plan</InfoLabel>
                  <InfoValue theme={theme}>{summary.licensePlanName}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel theme={theme}>Status</InfoLabel>
                  <LicenseBadge theme={theme} $status={summary.licenseStatus}>{summary.licenseStatus}</LicenseBadge>
                </InfoRow>
                {summary.licenseExpiryDate && (
                  <InfoRow>
                    <InfoLabel theme={theme}>Expires</InfoLabel>
                    <InfoValue theme={theme}>
                      {formatDate(summary.licenseExpiryDate)}
                      {summary.daysUntilExpiry != null && (
                        <span style={{ color: summary.daysUntilExpiry <= 30 ? '#ef4444' : theme.colors.muted, marginLeft: 6, fontSize: '12px' }}>
                          ({summary.daysUntilExpiry}d remaining)
                        </span>
                      )}
                    </InfoValue>
                  </InfoRow>
                )}
              </>
            ) : (
              <LocalEmptyState theme={theme}>No license assigned</LocalEmptyState>
            )}
          </Card>
        </CardGrid>

        <CardGrid theme={theme}>
          <Card theme={theme}>
            <CardTitle theme={theme}>User Breakdown</CardTitle>
            <InfoRow><InfoLabel theme={theme}>Active Users</InfoLabel><InfoValue theme={theme}>{summary.activeUsers}</InfoValue></InfoRow>
            <InfoRow><InfoLabel theme={theme}>Admins</InfoLabel><InfoValue theme={theme}>{summary.adminUsers}</InfoValue></InfoRow>
            <InfoRow><InfoLabel theme={theme}>Regular Users</InfoLabel><InfoValue theme={theme}>{summary.regularUsers}</InfoValue></InfoRow>
          </Card>

          <Card theme={theme}>
            <CardTitle theme={theme}>Submissions</CardTitle>
            <InfoRow><InfoLabel theme={theme}>Total</InfoLabel><InfoValue theme={theme}>{summary.totalSubmissions}</InfoValue></InfoRow>
            <InfoRow><InfoLabel theme={theme}>This Month</InfoLabel><InfoValue theme={theme}>{summary.submissionsThisMonth}</InfoValue></InfoRow>
            <InfoRow><InfoLabel theme={theme}>Customers</InfoLabel><InfoValue theme={theme}>{summary.totalCustomers}</InfoValue></InfoRow>
          </Card>
        </CardGrid>

        <Panel theme={theme}>
          <PanelHeader theme={theme}>
            <PanelTitle theme={theme}>Recent Submissions</PanelTitle>
          </PanelHeader>
          {(!summary.recentSubmissions || summary.recentSubmissions.length === 0) ? (
            <LocalEmptyState theme={theme}>No recent submissions</LocalEmptyState>
          ) : (
            (summary.recentSubmissions.$values || summary.recentSubmissions).map(sub => (
              <SubmissionItem key={sub.id} theme={theme}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: '13px', color: theme.colors.text }}>{sub.formName}</div>
                  <div style={{ fontSize: '11px', color: theme.colors.muted }}>
                    {sub.submitterName || sub.submitterEmail || 'Unknown'} &middot; {formatDateTime(sub.submittedAt)}
                  </div>
                </div>
                <LicenseBadge theme={theme} $status={sub.status === 'Submitted' ? 'Active' : sub.status === 'Approved' ? 'Active' : 'Trial'}>
                  {sub.status}
                </LicenseBadge>
              </SubmissionItem>
            ))
          )}
        </Panel>
      </>
    );
  };

  const renderUsers = () => (
    <>
      <SearchContainer theme={theme}>
        <StyledSearchContainer theme={theme}>
          <SearchIcon><Search size={16} /></SearchIcon>
          <TransparentInput
            placeholder="Search users..."
            value={userSearchTerm}
            onChange={(e) => setUserSearchTerm(e.target.value)}
            theme={theme}
          />
        </StyledSearchContainer>
        <ToggleSwitch theme={theme}>
          <ToggleInput
            type="checkbox"
            checked={userIncludeInactive}
            onChange={(e) => setUserIncludeInactive(e.target.checked)}
          />
          <ToggleSlider theme={theme} $checked={userIncludeInactive} />
          <ThemedText type="defaultSemiBold">Show inactive</ThemedText>
        </ToggleSwitch>
        <Button onClick={() => setIsCreateUserModalOpen(true)}>
          + Create User
        </Button>
      </SearchContainer>

      <SharedTableContainer theme={theme}>
        {usersLoading ? (
          <SkeletonTable rows={5} cols={5} theme={theme} />
        ) : filteredOrgUsers.length === 0 ? (
          <SharedEmptyState
            theme={theme}
            icon={<Users size={48} />}
            title="No users found"
            description={userSearchTerm ? 'Try adjusting your search term' : 'Create a user to get started'}
          />
        ) : (
          <SharedTable>
            <SharedThead theme={theme}>
              <tr>
                <SharedTh theme={theme}>User</SharedTh>
                <SharedTh theme={theme}>Email</SharedTh>
                <SharedTh theme={theme}>Role</SharedTh>
                <SharedTh theme={theme}>Status</SharedTh>
                <SharedTh theme={theme} style={{ width: '50px' }}></SharedTh>
              </tr>
            </SharedThead>
            <SharedTbody>
              {filteredOrgUsers.map(user => (
                <SharedTr key={user.id} theme={theme}>
                  <SharedTd theme={theme}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Avatar src={user.profilePicture} name={`${user.firstName} ${user.lastName}`} size="sm" />
                      <div>
                        <ThemedText type="defaultSemiBold">
                          {user.firstName} {user.lastName}
                        </ThemedText>
                        {user.phone && (
                          <ThemedText type="small" style={{ color: theme.colors.muted }}>
                            {user.phone}
                          </ThemedText>
                        )}
                      </div>
                    </div>
                  </SharedTd>
                  <SharedTd theme={theme}>
                    <ThemedText type="small">{user.email}</ThemedText>
                  </SharedTd>
                  <SharedTd theme={theme}>
                    <RoleBadge theme={theme} role={user.userRole !== undefined ? user.userRole : user.role}>
                      {getRoleName(user.userRole !== undefined ? user.userRole : user.role)}
                    </RoleBadge>
                  </SharedTd>
                  <SharedTd theme={theme}>
                    <SharedStatusBadge theme={theme} isActive={user.isActive}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </SharedStatusBadge>
                  </SharedTd>
                  <SharedTd theme={theme}>
                    <ActionMenu actions={[
                      { label: 'Edit', onClick: () => openEditUserModal(user) },
                      { label: 'Change Role', onClick: () => openRoleModal(user) },
                      { label: user.isActive ? 'Deactivate' : 'Activate', onClick: () => handleToggleUserActive(user) },
                      { label: 'Delete', onClick: () => setDeleteUserConfirmId(user.id), variant: 'danger' },
                    ]} />
                  </SharedTd>
                </SharedTr>
              ))}
            </SharedTbody>
          </SharedTable>
        )}
      </SharedTableContainer>

      {!usersLoading && orgUsers.length > 0 && (
        <Pagination
          currentPage={userCurrentPage}
          totalPages={userTotalPages}
          totalCount={userTotalCount}
          startIndex={userStartIndex}
          endIndex={userEndIndex}
          onPageChange={userGoToPage}
          itemLabel="users"
        />
      )}
    </>
  );

  const renderCustomers = () => (
    <Panel theme={theme}>
      <PanelHeader theme={theme}>
        <PanelTitle theme={theme}>Customers ({customerTotal})</PanelTitle>
        <SearchInput
          theme={theme}
          placeholder="Search customers..."
          value={customerSearch}
          onChange={(e) => { setCustomerSearch(e.target.value); setCustomerPage(1); }}
        />
      </PanelHeader>
      <TableContainer>
        {customersLoading ? (
          <LoadingState theme={theme}>Loading customers...</LoadingState>
        ) : customers.length === 0 ? (
          <LocalEmptyState theme={theme}>No customers found</LocalEmptyState>
        ) : (
          <Table>
            <Thead theme={theme}>
              <tr>
                <Th theme={theme}>Name</Th>
                <Th theme={theme}>Email</Th>
                <Th theme={theme}>Phone</Th>
                <Th theme={theme}>Company</Th>
                <Th theme={theme}>Created</Th>
              </tr>
            </Thead>
            <tbody>
              {customers.map(c => (
                <Tr key={c.id} theme={theme}>
                  <Td theme={theme} style={{ fontWeight: 500 }}>{c.firstName} {c.lastName}</Td>
                  <Td theme={theme}>{c.email || '-'}</Td>
                  <Td theme={theme}>{c.phone || '-'}</Td>
                  <Td theme={theme}>{c.company || '-'}</Td>
                  <Td theme={theme} style={{ whiteSpace: 'nowrap' }}>{formatDate(c.createdAt)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableContainer>
      {customerTotalPages > 1 && (
        <PaginationBar theme={theme}>
          <span>Page {customerPage} of {customerTotalPages} ({customerTotal} total)</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <PaginationButton
              theme={theme}
              disabled={customerPage <= 1}
              onClick={() => setCustomerPage(p => p - 1)}
            >
              Previous
            </PaginationButton>
            <PaginationButton
              theme={theme}
              disabled={customerPage >= customerTotalPages}
              onClick={() => setCustomerPage(p => p + 1)}
            >
              Next
            </PaginationButton>
          </div>
        </PaginationBar>
      )}
    </Panel>
  );

  return (
    <Layout currentPath={`/organizations/${id}`}>
      <PageContainer>
        <BackButton theme={theme} onClick={() => navigate('/organizations')}>
          <ArrowLeft size={14} /> Back to Organizations
        </BackButton>

        {loading ? (
          <LoadingState theme={theme}>Loading organization details...</LoadingState>
        ) : !summary ? (
          <LocalEmptyState theme={theme}>Organization not found</LocalEmptyState>
        ) : (
          <>
            <OrgHeader theme={theme}>
              <OrgTitle theme={theme}>
                {summary.name}
                <LocalStatusBadge $active={summary.isActive}>
                  {summary.isActive ? 'Active' : 'Inactive'}
                </LocalStatusBadge>
                {summary.isInternalTaskOrganization && (
                  <LocalStatusBadge $active style={{ backgroundColor: '#3b82f618', color: '#3b82f6' }}>
                    Internal Tasks
                  </LocalStatusBadge>
                )}
              </OrgTitle>
              <Button variant="secondary" size="sm" onClick={openEditOrgModal}>
                <Pencil size={14} /> Edit
              </Button>
            </OrgHeader>

            <TabBar theme={theme}>
              <Tab theme={theme} $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
                Overview
              </Tab>
              <Tab theme={theme} $active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
                Users
              </Tab>
              <Tab theme={theme} $active={activeTab === 'customers'} onClick={() => setActiveTab('customers')}>
                Customers
              </Tab>
              <Tab theme={theme} $active={activeTab === 'knowledge'} onClick={() => setActiveTab('knowledge')}>
                Knowledge Base
              </Tab>
            </TabBar>

            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'users' && renderUsers()}
            {activeTab === 'customers' && renderCustomers()}
            {activeTab === 'knowledge' && (
              <KnowledgeBaseTab
                organizationId={parseInt(id)}
                canManage={isSuperAdmin() || (userRole === UserRole.ORGANIZATION_ADMIN || userRole === UserRole.ADMIN)}
                theme={theme}
              />
            )}
          </>
        )}

        {/* Edit Organization Modal */}
        <Modal theme={theme} isOpen={isEditOrgModalOpen}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Edit Organization</ThemedText>
              <Button variant="secondary" size="sm" onClick={() => setIsEditOrgModalOpen(false)}>
                <X size={14} />
              </Button>
            </ModalHeader>
            <form onSubmit={handleUpdateOrganization}>
              <ModalBody theme={theme}>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Organization Name</Label>
                  <Input
                    value={editOrgFormData.name}
                    onChange={(e) => setEditOrgFormData({ ...editOrgFormData, name: e.target.value })}
                    placeholder="Enter organization name"
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Description</Label>
                  <Input
                    value={editOrgFormData.description}
                    onChange={(e) => setEditOrgFormData({ ...editOrgFormData, description: e.target.value })}
                    placeholder="Enter description"
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Contact Email</Label>
                  <Input
                    type="email"
                    value={editOrgFormData.contactEmail}
                    onChange={(e) => setEditOrgFormData({ ...editOrgFormData, contactEmail: e.target.value })}
                    placeholder="contact@example.com"
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Contact Phone</Label>
                  <Input
                    value={editOrgFormData.contactPhone}
                    onChange={(e) => setEditOrgFormData({ ...editOrgFormData, contactPhone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Address</Label>
                  <Input
                    value={editOrgFormData.address}
                    onChange={(e) => setEditOrgFormData({ ...editOrgFormData, address: e.target.value })}
                    placeholder="Enter address"
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <ToggleSwitch theme={theme}>
                    <ToggleInput
                      type="checkbox"
                      checked={editOrgFormData.isInternalTaskOrganization}
                      onChange={(e) => setEditOrgFormData({ ...editOrgFormData, isInternalTaskOrganization: e.target.checked })}
                    />
                    <ToggleSlider theme={theme} $checked={editOrgFormData.isInternalTaskOrganization} />
                    <Label theme={theme} style={{ cursor: 'pointer', marginBottom: 0 }}>Internal Task Organization</Label>
                  </ToggleSwitch>
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    Designates this organization as the internal task hub. Only one organization can be the internal task org.
                  </ThemedText>
                </FormGroup>
              </ModalBody>
              <ModalFooter theme={theme}>
                <Button type="button" variant="secondary" onClick={() => setIsEditOrgModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Update Organization</Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>

        {/* Create User Modal */}
        <Modal theme={theme} isOpen={isCreateUserModalOpen}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Create User</ThemedText>
              <Button variant="secondary" size="sm" onClick={() => { setIsCreateUserModalOpen(false); resetUserForm(); }}>
                <X size={14} />
              </Button>
            </ModalHeader>
            <form onSubmit={handleCreateUser}>
              <ModalBody theme={theme}>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Email *</Label>
                  <Input
                    required
                    type="email"
                    value={userFormData.email}
                    onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                    placeholder="user@example.com"
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>First Name *</Label>
                  <Input
                    required
                    value={userFormData.firstName}
                    onChange={(e) => setUserFormData({ ...userFormData, firstName: e.target.value })}
                    placeholder="Enter first name"
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Last Name *</Label>
                  <Input
                    required
                    value={userFormData.lastName}
                    onChange={(e) => setUserFormData({ ...userFormData, lastName: e.target.value })}
                    placeholder="Enter last name"
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Password *</Label>
                  <Input
                    required
                    type="password"
                    value={userFormData.password}
                    onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                    placeholder="Minimum 6 characters"
                    minLength={6}
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Role *</Label>
                  <Select
                    required
                    value={userFormData.userRole}
                    onChange={(e) => setUserFormData({ ...userFormData, userRole: parseInt(e.target.value) })}
                    theme={theme}
                  >
                    {getAvailableRoles(userRole).map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Phone</Label>
                  <Input
                    value={userFormData.phone}
                    onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter theme={theme}>
                <Button type="button" variant="secondary" onClick={() => { setIsCreateUserModalOpen(false); resetUserForm(); }}>
                  Cancel
                </Button>
                <Button type="submit">Create User</Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>

        {/* Edit User Modal */}
        <Modal theme={theme} isOpen={isEditUserModalOpen}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Edit User</ThemedText>
              <Button variant="secondary" size="sm" onClick={() => { setIsEditUserModalOpen(false); resetUserForm(); }}>
                <X size={14} />
              </Button>
            </ModalHeader>
            <form onSubmit={handleUpdateUser}>
              <ModalBody theme={theme}>
                <FormGroup theme={theme}>
                  <Label theme={theme}>First Name</Label>
                  <Input
                    value={userFormData.firstName}
                    onChange={(e) => setUserFormData({ ...userFormData, firstName: e.target.value })}
                    placeholder="Enter first name"
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Last Name</Label>
                  <Input
                    value={userFormData.lastName}
                    onChange={(e) => setUserFormData({ ...userFormData, lastName: e.target.value })}
                    placeholder="Enter last name"
                  />
                </FormGroup>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Phone</Label>
                  <Input
                    value={userFormData.phone}
                    onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter theme={theme}>
                <Button type="button" variant="secondary" onClick={() => { setIsEditUserModalOpen(false); resetUserForm(); }}>
                  Cancel
                </Button>
                <Button type="submit">Update User</Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>

        {/* Change Role Modal */}
        <Modal theme={theme} isOpen={isRoleModalOpen}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Change User Role</ThemedText>
              <Button variant="secondary" size="sm" onClick={() => { setIsRoleModalOpen(false); resetUserForm(); }}>
                <X size={14} />
              </Button>
            </ModalHeader>
            <form onSubmit={handleUpdateRole}>
              <ModalBody theme={theme}>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Role</Label>
                  <Select
                    value={userFormData.userRole}
                    onChange={(e) => setUserFormData({ ...userFormData, userRole: parseInt(e.target.value) })}
                    theme={theme}
                  >
                    {getAvailableRoles(userRole).map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </Select>
                </FormGroup>
              </ModalBody>
              <ModalFooter theme={theme}>
                <Button type="button" variant="secondary" onClick={() => { setIsRoleModalOpen(false); resetUserForm(); }}>
                  Cancel
                </Button>
                <Button type="submit">Update Role</Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>

        {/* Delete User Confirm */}
        <ConfirmDialog
          isOpen={deleteUserConfirmId !== null}
          title="Delete User"
          message="Are you sure you want to delete this user? This action cannot be undone."
          onConfirm={() => handleDeleteUser(deleteUserConfirmId)}
          onCancel={() => setDeleteUserConfirmId(null)}
        />
      </PageContainer>
    </Layout>
  );
};
